//
// # PowerRoutingDialog
//
// Modal dialog for configuring power mesh routing parameters before invoking
// the Rust mesh_power_router tool.
//
// Fields:
// - VDD Net: Dropdown if power nets available, else text input
// - GND Net: Dropdown if ground nets available, else text input
// - Lowest Layer: Dropdown from metal layers (excluding last)
// - Highest Layer: Dropdown filtered to layers above lowest
// - Power Track Period: Number input
// - Ground Track Period: Number input
// - Generate Rings: Checkbox
//

import React, { useState, useCallback, useMemo } from "react";
import { Modal } from "./Modal/Modal";

// The result returned when the user confirms the dialog.
export interface PowerRoutingConfig {
  readonly vddNet: string;
  readonly gndNet: string;
  readonly lowestLayerIndex: number;
  readonly highestLayerIndex: number;
  readonly powerTrackPeriod: number;
  readonly groundTrackPeriod: number;
  readonly generateRings: boolean;
}

interface PowerRoutingDialogProps {
  readonly powerNets: ReadonlyArray<string>;
  readonly groundNets: ReadonlyArray<string>;
  readonly metalLayers: ReadonlyArray<{
    readonly name: string;
    readonly number: number;
    readonly index: number;
  }>;
  readonly onConfirm: (config: PowerRoutingConfig) => void;
  readonly onCancel: () => void;
}

export function PowerRoutingDialog({
  powerNets,
  groundNets,
  metalLayers,
  onConfirm,
  onCancel,
}: PowerRoutingDialogProps) {
  // VDD/GND net state - default to first available or common defaults
  const [vddNet, setVddNet] = useState(
    powerNets.length > 0 ? powerNets[0] : "VDD"
  );
  const [gndNet, setGndNet] = useState(
    groundNets.length > 0 ? groundNets[0] : "VSS"
  );

  // Default to top 3 layers for power mesh (or all layers if fewer than 3).
  // Upper metals are typically wider and better suited for power distribution.
  const numPowerLayers = Math.min(3, metalLayers.length);
  const defaultLowestIdx = Math.max(0, metalLayers.length - numPowerLayers);
  const defaultHighestIdx = metalLayers.length - 1;
  const [lowestLayerIndex, setLowestLayerIndex] = useState(defaultLowestIdx);
  const [highestLayerIndex, setHighestLayerIndex] = useState(defaultHighestIdx);

  // Track period state - stored as strings to allow free editing
  const [powerTrackPeriodStr, setPowerTrackPeriodStr] = useState("5");
  const [groundTrackPeriodStr, setGroundTrackPeriodStr] = useState("5");

  // Parse track periods as numbers for validation and submission
  const powerTrackPeriod = parseInt(powerTrackPeriodStr, 10) || 0;
  const groundTrackPeriod = parseInt(groundTrackPeriodStr, 10) || 0;

  // Ring generation state
  const [generateRings, setGenerateRings] = useState(false);

  // Filter highest layer options to only show layers above the lowest.
  // Uses strict > (not >=) because power mesh requires at least 2 different layers
  // for orthogonal horizontal/vertical stripes with via connections.
  const highestLayerOptions = useMemo(() => {
    return metalLayers.filter((layer) => layer.index > lowestLayerIndex);
  }, [metalLayers, lowestLayerIndex]);

  // Validation
  const isValid = useMemo(() => {
    return (
      vddNet.trim() !== "" &&
      gndNet.trim() !== "" &&
      highestLayerIndex > lowestLayerIndex &&
      powerTrackPeriod >= 1 &&
      groundTrackPeriod >= 1
    );
  }, [
    vddNet,
    gndNet,
    highestLayerIndex,
    lowestLayerIndex,
    powerTrackPeriod,
    groundTrackPeriod,
  ]);

  const handleConfirm = useCallback(() => {
    if (!isValid) return;
    onConfirm({
      vddNet: vddNet.trim(),
      gndNet: gndNet.trim(),
      lowestLayerIndex,
      highestLayerIndex,
      powerTrackPeriod,
      groundTrackPeriod,
      generateRings,
    });
  }, [
    isValid,
    vddNet,
    gndNet,
    lowestLayerIndex,
    highestLayerIndex,
    powerTrackPeriod,
    groundTrackPeriod,
    generateRings,
    onConfirm,
  ]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && isValid) {
        e.preventDefault();
        handleConfirm();
      }
    },
    [isValid, handleConfirm]
  );

  // When lowest layer changes, ensure highest layer is still valid
  const handleLowestLayerChange = useCallback(
    (newIndex: number) => {
      setLowestLayerIndex(newIndex);
      // If highest is now <= lowest, bump it to lowest + 1
      if (highestLayerIndex <= newIndex) {
        const nextValidIdx = metalLayers.findIndex((l) => l.index > newIndex);
        if (nextValidIdx >= 0) {
          setHighestLayerIndex(metalLayers[nextValidIdx].index);
        }
      }
    },
    [highestLayerIndex, metalLayers]
  );

  const selectStyle = {
    backgroundColor: "var(--color-ide-surface-2)",
    borderColor: "var(--color-ide-border)",
    color: "var(--color-ide-text)",
  };

  const inputStyle = {
    backgroundColor: "var(--color-ide-surface-2)",
    borderColor: "var(--color-ide-border)",
    color: "var(--color-ide-text)",
  };

  return (
    <Modal open={true} onClose={onCancel} title="Configure Power Routing">
      <div onKeyDown={handleKeyDown} className="flex flex-col gap-3">
        {/* VDD Net */}
        <div className="flex flex-col gap-1">
          <label
            className="text-[12px]"
            style={{ color: "var(--color-ide-text-muted)" }}
          >
            VDD Net
          </label>
          {powerNets.length > 0 ? (
            <select
              value={vddNet}
              onChange={(e) => setVddNet(e.target.value)}
              className="px-2 py-1.5 text-[13px] rounded border outline-none"
              style={selectStyle}
            >
              {powerNets.map((net) => (
                <option key={net} value={net}>
                  {net}
                </option>
              ))}
            </select>
          ) : (
            <input
              type="text"
              value={vddNet}
              onChange={(e) => setVddNet(e.target.value)}
              placeholder="VDD"
              className="px-2 py-1.5 text-[13px] rounded border outline-none"
              style={inputStyle}
            />
          )}
        </div>

        {/* GND Net */}
        <div className="flex flex-col gap-1">
          <label
            className="text-[12px]"
            style={{ color: "var(--color-ide-text-muted)" }}
          >
            GND Net
          </label>
          {groundNets.length > 0 ? (
            <select
              value={gndNet}
              onChange={(e) => setGndNet(e.target.value)}
              className="px-2 py-1.5 text-[13px] rounded border outline-none"
              style={selectStyle}
            >
              {groundNets.map((net) => (
                <option key={net} value={net}>
                  {net}
                </option>
              ))}
            </select>
          ) : (
            <input
              type="text"
              value={gndNet}
              onChange={(e) => setGndNet(e.target.value)}
              placeholder="VSS"
              className="px-2 py-1.5 text-[13px] rounded border outline-none"
              style={inputStyle}
            />
          )}
        </div>

        {/* Layer Selection */}
        {metalLayers.length >= 2 && (
          <>
            <div className="flex flex-col gap-1">
              <label
                className="text-[12px]"
                style={{ color: "var(--color-ide-text-muted)" }}
              >
                Lowest Metal Layer
              </label>
              <select
                value={lowestLayerIndex}
                onChange={(e) =>
                  handleLowestLayerChange(Number(e.target.value))
                }
                className="px-2 py-1.5 text-[13px] rounded border outline-none"
                style={selectStyle}
              >
                {metalLayers
                  .filter((_, idx) => idx < metalLayers.length - 1)
                  .map((layer) => (
                    <option key={layer.number} value={layer.index}>
                      {layer.name}
                    </option>
                  ))}
              </select>
            </div>

            <div className="flex flex-col gap-1">
              <label
                className="text-[12px]"
                style={{ color: "var(--color-ide-text-muted)" }}
              >
                Highest Metal Layer
              </label>
              <select
                value={highestLayerIndex}
                onChange={(e) => setHighestLayerIndex(Number(e.target.value))}
                className="px-2 py-1.5 text-[13px] rounded border outline-none"
                style={selectStyle}
              >
                {highestLayerOptions.map((layer) => (
                  <option key={layer.number} value={layer.index}>
                    {layer.name}
                  </option>
                ))}
              </select>
            </div>
          </>
        )}

        {/* Track Period Settings */}
        <div className="flex gap-3">
          <div className="flex-1 flex flex-col gap-1">
            <label
              className="text-[12px]"
              style={{ color: "var(--color-ide-text-muted)" }}
            >
              Power Track Period
            </label>
            <input
              type="number"
              min={1}
              value={powerTrackPeriodStr}
              onChange={(e) => setPowerTrackPeriodStr(e.target.value)}
              onBlur={() => {
                // Reset to default of 5 if invalid
                const val = parseInt(powerTrackPeriodStr, 10);
                if (isNaN(val) || val < 1) {
                  setPowerTrackPeriodStr("5");
                }
              }}
              className="px-2 py-1.5 text-[13px] rounded border outline-none"
              style={inputStyle}
            />
          </div>
          <div className="flex-1 flex flex-col gap-1">
            <label
              className="text-[12px]"
              style={{ color: "var(--color-ide-text-muted)" }}
            >
              Ground Track Period
            </label>
            <input
              type="number"
              min={1}
              value={groundTrackPeriodStr}
              onChange={(e) => setGroundTrackPeriodStr(e.target.value)}
              onBlur={() => {
                // Reset to default of 5 if invalid
                const val = parseInt(groundTrackPeriodStr, 10);
                if (isNaN(val) || val < 1) {
                  setGroundTrackPeriodStr("5");
                }
              }}
              className="px-2 py-1.5 text-[13px] rounded border outline-none"
              style={inputStyle}
            />
          </div>
        </div>

        {/* Generate Rings Checkbox */}
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="generate-rings"
            checked={generateRings}
            onChange={(e) => setGenerateRings(e.target.checked)}
            className="w-4 h-4"
          />
          <label
            htmlFor="generate-rings"
            className="text-[13px]"
            style={{ color: "var(--color-ide-text)" }}
          >
            Generate Power/Ground Rings
          </label>
        </div>

        {/* Validation Error */}
        {!isValid && (
          <div
            className="text-[12px] mt-1"
            style={{ color: "var(--color-ide-error)" }}
          >
            {highestLayerIndex <= lowestLayerIndex
              ? "Highest layer must be above lowest layer"
              : vddNet.trim() === "" || gndNet.trim() === ""
              ? "Net names cannot be empty"
              : "Invalid configuration"}
          </div>
        )}

        {/* Buttons */}
        <div className="flex justify-end gap-2 mt-3">
          <button
            onClick={onCancel}
            className="px-4 py-1.5 text-[13px] rounded border cursor-pointer bg-transparent"
            style={{
              borderColor: "var(--color-ide-border)",
              color: "var(--color-ide-text)",
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!isValid}
            className="px-4 py-1.5 text-[13px] rounded border-none cursor-pointer"
            style={{
              backgroundColor: isValid
                ? "var(--color-ide-accent)"
                : "var(--color-ide-surface-3)",
              color: isValid ? "#ffffff" : "var(--color-ide-text-muted)",
              cursor: isValid ? "pointer" : "not-allowed",
            }}
          >
            Run Power Routing
          </button>
        </div>
      </div>
    </Modal>
  );
}
